import { JSON_SCHEMA_FORMATS, JSON_SCHEMA_TYPES, JsonSchemaModel } from '../Models/JsonSchemaModel';
import { getSchemaAlternatives, schemaMatchesValue } from './JsonSchemaTypes';

/**
 * Generates a sample JSON object that conforms to the given JSON schema.
 * Useful for giving users a guided starting point when filling in input fields.
 */
/**
 * Returns true when all required fields (recursively) in the schema are present
 * and non-empty in the given value.
 */
export function validateRequiredFields(schema: JsonSchemaModel, value: Record<string, unknown>): boolean {
	return schemaMatchesValue(schema, value);
}

export function generateSampleJsonFromSchema(schema: JsonSchemaModel): unknown {
	if ('const' in schema) return schema.const;
	if (schema.anyOf || schema.oneOf || Array.isArray(schema.type)) {
		const alternatives = getSchemaAlternatives(schema);
		return generateSampleJsonFromSchema(alternatives.find((branch) => branch.type !== 'null') ?? alternatives[0]);
	}
	switch (schema.type) {
		case JSON_SCHEMA_TYPES.OBJECT:
			return generateSampleObject(schema);

		case JSON_SCHEMA_TYPES.ARRAY:
			return generateSampleArray(schema);

		case JSON_SCHEMA_TYPES.STRING:
			return generateSampleString(schema);

		case JSON_SCHEMA_TYPES.NUMBER:
			return 0.0;

		case JSON_SCHEMA_TYPES.INTEGER:
			return 0;

		case JSON_SCHEMA_TYPES.BOOLEAN:
			return false;

		case JSON_SCHEMA_TYPES.NULL:
			return null;

		default:
			return null;
	}
}

function generateSampleObject(schema: JsonSchemaModel): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	if (!schema.properties) return result;

	for (const [propName, propSchema] of Object.entries(schema.properties)) {
		result[propName] = generateSampleJsonFromSchema(propSchema);
	}

	return result;
}

function generateSampleArray(schema: JsonSchemaModel): unknown[] {
	if (!schema.items || Array.isArray(schema.items)) {
		return [];
	}
	return [generateSampleJsonFromSchema(schema.items)];
}

function generateSampleString(schema: JsonSchemaModel): string {
	// Use first enum value if available
	if (schema.enum && schema.enum.length > 0) {
		return String(schema.enum[0]);
	}

	switch (schema.format) {
		case JSON_SCHEMA_FORMATS.UUID:
			return '00000000-0000-0000-0000-000000000000';
		case JSON_SCHEMA_FORMATS.DATE_TIME:
			return new Date().toISOString();
		case JSON_SCHEMA_FORMATS.DATE:
			return new Date().toISOString().slice(0, 10);
		case JSON_SCHEMA_FORMATS.TIME:
			return new Date().toISOString().slice(11, 19);
		case JSON_SCHEMA_FORMATS.EMAIL:
			return 'user@example.com';
		case JSON_SCHEMA_FORMATS.URI:
			return 'https://example.com';
		default:
			return '';
	}
}
